import React, { useState, useEffect } from 'react';
import BsModal from '../Common/BsModal';
import ajaxGym from '../../util/remote/ajaxGym';
import functions from '../../util/functions';
import { useGym } from '../../DataStore/Gym/GymContext';
import { useTheme } from '../../DataStore/ThemeContext';
import Notification from '../Common/Notification';
import ActivityLoader from '../Common/ActivityLoader';
import TextInput from '../Common/TextInput';
import { useBalance } from '../../DataStore/BalanceContext';

const GymAdmission = ({ show, onClose, onStartSession, customer }) => {
  const [sessionType, setSessionType] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentAccount, setPaymentAccount] = useState("");
  const [reference, setReference] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [amountDue, setAmountDue] = useState(0);
  const [trainerCommission, setTrainerCommission] = useState('');
  const [loadingBill, setLoadingBill] = useState(false);
  const [description, setDescription] = useState('');
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const { setSweetDialogInfo } = useTheme();
  const { getScheduledClasses, scheduledClasses, getDailySessionActivity, refreshSessions, getTrainerList, trainerList } = useGym();
  const { getCashBankListing, cashBankListing } = useBalance();

  useEffect(() => {
    getScheduledClasses();
    getCashBankListing();
    getTrainerList();
  }, []);

  useEffect(() => {
    if (sessionType && customer) {
      calculateAmountDue();
    }
  }, [sessionType, customer]);

  const calculateAmountDue = async() => {
    setLoadingBill(true);
    const server_response = await ajaxGym.fetchSessionPrice(customer.id, sessionType);
    setLoadingBill(false);
    if (server_response.status === "OK") {
      setAmountDue(server_response.details.total);
      setAmountPaid(server_response.details.total);
      return server_response.details.total;
    } else {
      setNotify(<Notification type='danger' message={server_response.message} />);
      return 0;
    }
  };

  const handleStart = async () => {
    if(loading){
      setNotify(<Notification type='danger' message="Please wait while we process your request." />);
      return;
    }
    if(sessionType.length === 0) {
      setNotify(<Notification type='danger' message="Please select a session type." />);
      return;
    }
    if(loadingBill){
      setNotify(<Notification type='danger' message="Please wait while we calculate the amount due." />);
      return;
    }
    if(amountPaid > amountDue) {
      setNotify(<Notification type='danger' message="Amount paid cannot be greater than the total bill." />);
      return;
    }
    if(!paymentAccount && amountPaid > 0) {
      setNotify(<Notification type='danger' message="Please select a payment account." />);
      return;
    }

    setLoading(true);
    setNotify(null);
    const data = {
      member_id: customer.id,
      class_schedule_id: selectedSchedule,
      session_type: sessionType,
      trainer_id: selectedTrainer?.id,
      payment_account: paymentAccount?.value,
      reference: reference,
      description: description,
      amount_paid: amountPaid,
      commission: trainerCommission,
    };
    const server_response = await ajaxGym.startNewSession(data);
    setLoading(false);
    if (server_response.status === "OK") {
      getDailySessionActivity();
      setSessionType('');
      setReference('');
      setDescription('');
      setSelectedSchedule(null);
      setSelectedTrainer(null);
      setNotify(<Notification type='success' message={server_response.message} />);
      setSweetDialogInfo(server_response.message, customer.full_name + " " + customer.phone_number);
      refreshSessions();
      onClose();
    } else {
      setNotify(<Notification type='danger' message={server_response.message} />);
    }
    setTimeout(() => {
      setNotify(null);
    }, 5000);
  };

  const RenderAccountField = () => {
    const options = [];
    if (Array.isArray(cashBankListing)) {
      cashBankListing.map((item, key) =>
        options.push({
          label: item.account_number + " " + item.account_name,
          value: item.account_id,
        })
      );
    }
    return (
      <TextInput
        label="Cash Account"
        type="select"
        leftIcon="fa fa-university"
        placeholder={paymentAccount.label || "Select account..."}
        options={options}
        onChange={(item) => setPaymentAccount(item)}
        leftComponent={<i className="fa fa-university" onClick={() => getCashBankListing(true)}></i>}
        loading={!cashBankListing}
      />
    );
  };

  return (
    <BsModal
      show={show}
      onClose={onClose}
      title="Stage Gym Session"
      size="modal-xl"
      footer={
        <>
          {!loading ? (
            <>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleStart} disabled={!sessionType && !selectedSchedule}>
                <i className="fa fa-play-circle mr-2"></i> Start Session
              </button>
            </>
          ) : <ActivityLoader />}
        </>
      }
    >
      <div className="row">
        <div className="col-md-6">
          <div className="mb-4">
            <h6 className="text-muted font-weight-bold mb-3">
              <i className="fa fa-user-circle mr-2 text-primary"></i>Customer Info
            </h6>
            <p className="mb-1 text-muted">
              <strong>Name:</strong> <span className="text-dark">{customer?.full_name}</span><br />
              <strong>Phone:</strong> <span className="text-dark">{customer?.phone_number}</span><br />
              <strong>Membership Expiry:</strong> <span className="text-dark">{customer?.membership_expiry ? functions.formatDate(customer.membership_expiry) : 'N.A'}</span>
            </p>
          </div>
          {notify}
          <div className="mb-4">
            <h6 className="font-weight-bold text-dark mb-3">Select Admission Option</h6>
            <div className="d-flex justify-content-around flex-wrap">
              <div className={`p-4 text-center rounded shadow-sm m-2 admission-option ${sessionType === 'walkin' ? 'active' : ''}`} onClick={() => { setSessionType('walkin'); setSelectedSchedule(null); }}>
                <i className="fa fa-dumbbell fa-2x mb-2"></i>
                <div className="font-weight-bold">Gym Training</div>
              </div>
              <div className={`p-4 text-center rounded shadow-sm m-2 admission-option ${sessionType === 'private' ? 'active' : ''}`} onClick={() => { setSessionType('private'); setSelectedSchedule(null); }}>
                <i className="fa fa-user-tie fa-2x mb-2"></i>
                <div className="font-weight-bold">Private Training</div>
              </div>
            </div>
          </div>
          <div className="mb-4">
            <h6 className="font-weight-bold text-dark mb-3">Select Gym Class</h6>
            <div className="d-flex flex-wrap">
              {Array.isArray(scheduledClasses) && scheduledClasses.map(cls => (
                <div key={cls.id} className={`p-3 m-2 rounded shadow-sm text-center gym-class-option ${selectedSchedule === cls.id ? 'active' : ''}`} onClick={() => { setSessionType('class'); setSelectedSchedule(cls.id); }}>
                  <div className="font-weight-bold">{cls.class.class_name}</div>
                  <div className="small">{cls.day_of_week_name}, {cls.scheduled_time}</div>
                </div>
              ))}
            </div>
          </div>
         
        </div>
        <div className="col-md-6">
          <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
            <h5 className="text-center text-dark font-weight-bold mb-4">Payment Details</h5>
            <div className="mb-3">
              <strong>Total Bill:</strong> UGX {loadingBill ? '...' : functions.numberFormat(amountDue)} <br />
              <strong>Total Paid:</strong> UGX {functions.numberFormat(amountPaid)} <br />
            </div>
          
            {amountDue * 1 > 0 && <>
              <TextInput
                type="number"
                label="Amount Paid"
                placeholder="Enter amount paid"
                leftIcon="fa fa-credit-card"
                value={amountPaid}
                onChange={e => setAmountPaid(e.target.value)}
                required
              />
              {RenderAccountField()}
              <TextInput
                type="text"
                label="Transaction Ref"
                placeholder="Transaction Ref"
                leftIcon="fa fa-hashtag"
                value={reference}
                onChange={e => setReference(e.target.value)}
                required
              />
              {amountPaid * 1 !== amountDue * 1 && <TextInput
                type="textarea"
                label="Session notes"
                placeholder="Add any notes about the session..."
                leftIcon="fa fa-list"
                value={description}
                onChange={e => setDescription(e.target.value)}
                required
              />}
            </>}
            <TextInput
                type="number"
                label="T. Commission"
                placeholder="Enter trainer commission"
                leftIcon="fa fa-money-bill-wave"
                value={trainerCommission}
                onChange={e => setTrainerCommission(e.target.value)}
                required
              />


          <div className="mb-4 mt-4">
            <h6 className="font-weight-bold text-dark mb-3">Select Trainer</h6>
            <div className="d-flex overflow-auto" style={{ gap: '1rem' }}>
              {Array.isArray(trainerList) && trainerList.map(tr => (
                <div
                  key={tr.id}
                  className={`p-2 text-center border rounded shadow-sm ${selectedTrainer?.id === tr.id ? 'border-primary' : ''}`}
                  style={{ minWidth: '150px', cursor: 'pointer' }}
                  onClick={() => setSelectedTrainer(tr)}
                >
                  <img src={tr.photo} alt={tr.full_name} className="rounded-circle mb-2" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                  <div className="font-weight-bold small">{tr.full_name}</div>
                  <small className="text-muted">UGX {functions.numberFormat(tr.commission_balance)}</small>
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      </div>
    </BsModal>
  );
};

export default GymAdmission;
